import { VOMelody, VOMelodyPair, VOMelodyPairItem } from "../model/DataStructure.js";
import { Labels } from "../model/Labels.js";
import { Decoration } from "./Decoration.js";
import { DiamondButton } from "./DiamondButton.js";

export interface MelodySelectorOptions {
    container: HTMLElement;
    labels: Labels;
    item?: VOMelodyPair;
    onSelect?: (m: VOMelodyPairItem) => void;
    onStart?: (m: VOMelodyPairItem) => void;
}

export class MelodySelector {

    public readonly onSelect = e5.Signal.create<[VOMelodyPairItem, number]>();
    public readonly onStart = e5.Signal.create<[VOMelodyPairItem, number]>();

    private _element: HTMLElement;
    private _options: MelodySelectorOptions;
    private _melody: VOMelodyPairItem = null;
    private _item: VOMelodyPair = null;
    private _decoration: Decoration;
    private _start: DiamondButton;
    private _image: HTMLElement;
    private _level: number = 0;

    constructor(options: MelodySelectorOptions) {
        options = e5.object.merge({
        }, options);
        this._options = options;

        this._element = document.createElement("div");
        this._element.className = "melody-selector melody-selector--hidden";
        this._element.innerHTML = `
        <div class="melody-selector__decoration"></div>
        <div class="melody-selector__box">
            <div class="melody-selector__list"></div>
            <div class="melody-selector__footer"></div>
        </div>
        <div class="melody-selector__image"></div>
        <div class="melody-selector__headline">${options.labels.text("melody.melody_instrument.headline")}</div>
        `;

        this._image = this._element.querySelector(".melody-selector__image");

        this._start = new DiamondButton({
            label: "Start",
            container: this._element.querySelector(".melody-selector__footer"),
            visible: false,
            className: "melody-selector__start-button",
            onClick: () => {
                if (!this.melody
                    || !this.visible) {
                    return;
                }
                this.onStart.dispatch(this.melody, this._level);
            }
        });

        this._decoration = new Decoration({
            container: this._element.querySelector(".melody-selector__decoration"),
            visible: false,
            names: [
                "melody-selector-1",
                "melody-selector-2",
                "melody-selector-10",
                "melody-selector-13"
            ]
        });

        if (options.item) {
            this.item = options.item;
        }

        if (typeof options.onSelect === "function") {
            this.onSelect.add(options.onSelect);
        }

        if (typeof options.onStart === "function") {
            this.onStart.add(options.onStart);
        }

        e5.event.on({
            type: "click",
            scope: this._element,
            selector: ".melody-selector__item[data-id][data-level]",
            listener: (e) => {
                if (!this.visible) {
                    return;
                }
                const id = parseInt(e.target.getAttribute("data-id"));
                this._level = parseInt(e.target.getAttribute("data-level"));
                this.melody = this._item?.pairs.find((m) => m.id === id);
            }
        });

        if (options.container) {
            options.container.appendChild(this._element);
        }
    }

    public get item(): VOMelodyPair {
        return this._item;
    }

    public set item(value: VOMelodyPair) {
        if (this._item === value) {
            return;
        }
        this.melody = null;
        this._item = value;
        const imageUrl = value?.image?.url;
        this._image.style.backgroundImage = imageUrl ? `url(${imageUrl})` : "none";
        this._element.style.setProperty("--accent-color", value?.color || "#666");

        this.render(value?.pairs || []);
    }

    private render(pairs: VOMelodyPairItem[]): void {
        const html: string[] = pairs.map((pair, index, array) => {
            return `
            <div class="melody-selector__item" data-id="${pair.id}" data-level="${index + 1}">
                <div class="melody-selector__item-body">
                    <div class="melody-selector__item-content melody-selector__item-content--start">
                        <div class="melody-selector__item-icon e5 icon-682"></div>
                    </div>
                    <div class="melody-selector__item-content melody-selector__item-content--middle">
                        <div class="melody-selector__item-level">Level <span>${"\\".repeat(index + 1)}</span></div>
                    </div>
                    <div class="melody-selector__item-content melody-selector__item-content--end">
                        <div class="melody-selector__item-title">${e5.Text.escapeHtml(pair.melody.name)}</div>
                        <div class="melody-selector__item-subtitle">${e5.Text.escapeHtml(pair.melody.subtitle)}</div>
                    </div>
                </div>
            </div>
            ${index < array.length - 1 ? `<div class="melody-selector__item-separator"></div>` : ""}`;
        });

        this._element.querySelector(".melody-selector__list").innerHTML = html.join("");
    }

    public get melody(): VOMelodyPairItem {
        return this._melody;
    }

    public set melody(value: VOMelodyPairItem) {
        if (this._melody?.id == value?.id) {
            return;
        }

        if (this._melody) {
            this.getItem(this._melody)?.classList.remove("melody-selector__item--active");
        }

        this._melody = value;

        if (this._melody) {
            this.getItem(this._melody)?.classList.add("melody-selector__item--active");
        }

        // this._start.visible = Boolean(this._melody);
        if (value) {
            this.onStart.dispatch(value, this._level);
        }

        this.onSelect.dispatch(value, this._level);
    }

    private getItem(melody: VOMelodyPairItem): HTMLElement {
        return this._element.querySelector(`.melody-selector__item[data-id="${melody.id}"]`);
    }

    public get visible(): boolean {
        return !this._element.classList.contains("melody-selector--hidden");
    }

    public set visible(value: boolean) {
        this._element.classList.toggle("melody-selector--hidden", !value);
        this._decoration.visible = value;
    }
}